import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth, type User } from "../contexts/AuthContext";
import { useCertificationSession } from "../components/AccreditedInvestorGate";
import { analyticsService } from "../services/analyticsService";
import { TrackingEvents } from "../services/trackingEvents";
import { trackingService } from "../services/trackingService";
import { useGateNudge, GATE_EMAIL_PATTERN, type GateNudgeReason } from "./useGateNudge";
import { recordGateCertification } from "./useProductShareGateFlow";

// Cohort tag for the auth funnel, beside AuthPageV2's "v2" and the share
// page's "share-gate-v5": sign-ups that started in the homepage hero.
export const HOME_GATE_PAGE_VERSION = "home-gate-v1" as const;
const HOME_GATE_SURFACE = "landing_hero_gate" as const;
const RESEND_COOLDOWN_SECONDS = 120;

export type HomeGateStep = "email" | "code";

export interface HomeGateFlow {
  step: HomeGateStep;
  email: string;
  nudge: { reason: GateNudgeReason | null; sequence: number };
  otp: string;
  error: string;
  isSubmitting: boolean;
  resendCooldown: number;
  setEmail: (value: string) => void;
  setOtp: (value: string) => void;
  /**
   * First touch of the gate (email or Google). Opens the
   * sign-up funnel once per mount; later calls are no-ops. Mounting alone
   * does not count: every anonymous homepage visitor mounts the gate.
   */
  engage: () => void;
  submitEmail: () => Promise<void>;
  submitCode: () => Promise<void>;
  /** After Google signs the person in: the same chain as a good code. */
  completeGoogleSignIn: (user: User | undefined) => void;
  resendCode: () => Promise<void>;
  editEmail: () => void;
}

export interface UseHomeGateFlowParams {
  /** CTA campaign prefix, e.g. "landing_hero_gate"; each step appends its own suffix. */
  campaign: string;
  /** Analytics section the gate sits in, e.g. "Landing Hero". */
  section: string;
  pageVersion?: string;
  /** A separate homepage route can finish its explicit action before routing. */
  onSignedIn?: (user: User) => void;
}

/**
 * The share page's sign-up state machine, on the homepage: email and the
 * certification notice, then the 6-digit code in the same spot, resend on a
 * cooldown, a way back to the email. The homepage only renders for anonymous
 * visitors (BaseRouteHandler), and the same handler routes a signed-in
 * visitor to their landing page, so once the session exists the gate does
 * not navigate itself: BaseRouteHandler re-renders with the fresh user and
 * roles and picks the destination. (Navigating from here would read the
 * pre-login render's state and send every existing account to /alpha.)
 * The funnel opens on the first touch of the gate, so a visitor who only
 * reads the page is not a sign-up start.
 */
export function useHomeGateFlow({ campaign, section, pageVersion: configuredPageVersion, onSignedIn }: UseHomeGateFlowParams): HomeGateFlow {
  const pageVersion = configuredPageVersion ?? HOME_GATE_PAGE_VERSION;
  const { sendOtp, verifyOtp } = useAuth();
  const { trackPendingCertificationSave } = useCertificationSession();
  const { nudge, pulse, clear } = useGateNudge();
  const [step, setStep] = useState<HomeGateStep>("email");
  const [email, setEmailRaw] = useState("");
  const [otp, setOtpRaw] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownTimerRef = useRef<number | null>(null);
  const requestIdRef = useRef(0);
  const engagedRef = useRef(false);
  const onSignedInRef = useRef(onSignedIn);
  onSignedInRef.current = onSignedIn;

  // The hero-engagement CTA event stands where the old arrow's click event
  // was in the funnel; the cohort tag and the funnel open travel with it.
  const engage = useCallback(() => {
    if (engagedRef.current) return;
    engagedRef.current = true;
    analyticsService.trackCTAClick({ campaign, label: "Hero gate", section, destination: "#home-gate", intent: "signup", ...(configuredPageVersion ? { pageVersion: configuredPageVersion } : {}) });
    trackingService.registerSessionSuperProperties({ page_version: pageVersion, mode: "signup" });
    analyticsService.trackSignupStart();
  }, [campaign, section, pageVersion, configuredPageVersion]);

  useEffect(() => () => {
    if (!engagedRef.current) return;
    trackingService.unregisterSuperProperty("page_version");
    trackingService.unregisterSuperProperty("mode");
  }, []);

  useEffect(() => () => {
    if (cooldownTimerRef.current !== null) window.clearInterval(cooldownTimerRef.current);
  }, []);

  const startCooldown = useCallback(() => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    if (cooldownTimerRef.current !== null) window.clearInterval(cooldownTimerRef.current);
    cooldownTimerRef.current = window.setInterval(() => {
      setResendCooldown((previous) => {
        if (previous <= 1) {
          if (cooldownTimerRef.current !== null) window.clearInterval(cooldownTimerRef.current);
          cooldownTimerRef.current = null;
          return 0;
        }
        return previous - 1;
      });
    }, 1000);
  }, []);

  const emailDomain = email.split("@")[1] || "unknown";

  const submitEmail = useCallback(async () => {
    if (isSubmitting) return;
    engage();
    // Missing input gets a bronze nudge; transport and code errors keep their existing messages.
    if (!GATE_EMAIL_PATTERN.test(email)) { pulse("email"); return; }
    const requestId = ++requestIdRef.current;
    setIsSubmitting(true);
    setError("");
    analyticsService.trackCTAClick({ campaign: `${campaign}_email_submit`, label: "Continue", section, destination: "otp", intent: "signup", ...(configuredPageVersion ? { pageVersion: configuredPageVersion } : {}) });

    const result = await sendOtp(email, "signup");
    if (requestId !== requestIdRef.current) return;
    if (result.success) {
      analyticsService.trackSignupEmailSubmitted(email);
      setOtpRaw("");
      setStep("code");
      startCooldown();
    } else {
      setError(result.message || "We could not send a code. Try again.");
    }
    setIsSubmitting(false);
  }, [campaign, email, engage, isSubmitting, section, sendOtp, startCooldown, pulse, configuredPageVersion]);

  // BaseRouteHandler redirects after sign-in; keep the pending save in the
  // shared provider so Alpha waits for it before checking certification.
  const finishSignIn = useCallback((user: User, method: "email_otp" | "google_sso") => {
    analyticsService.trackSignupComplete(user.id, method);
    void trackPendingCertificationSave(user.id, recordGateCertification(user.email || email, HOME_GATE_SURFACE));
    onSignedInRef.current?.(user);
  }, [email, trackPendingCertificationSave]);

  const submitCode = useCallback(async () => {
    if (otp.length !== 6 || isSubmitting) return;
    const requestId = ++requestIdRef.current;
    setIsSubmitting(true);
    setError("");
    analyticsService.trackCTAClick({ campaign: `${campaign}_code_submit`, label: "Continue", section, destination: "app", intent: "signup", ...(configuredPageVersion ? { pageVersion: configuredPageVersion } : {}) });
    trackingService.track(TrackingEvents.AUTH_SIGNUP_OTP_SUBMITTED, {
      email_domain: emailDomain,
      page_version: pageVersion,
      mode: "signup",
    });

    const result = await verifyOtp(email, otp, "signup");
    if (requestId !== requestIdRef.current) return;
    if (result.success && result.user) {
      finishSignIn(result.user, "email_otp");
      return;
    }
    setError(result.message || "That code did not work. Check it and try again.");
    setIsSubmitting(false);
  }, [campaign, email, emailDomain, finishSignIn, isSubmitting, otp, section, verifyOtp, pageVersion, configuredPageVersion]);

  // The notice applies to Google too; save only after authentication succeeds.
  const completeGoogleSignIn = useCallback((user: User | undefined) => {
    if (!user) return;
    engage();
    analyticsService.trackCTAClick({ campaign: `${campaign}_google`, label: "Continue with Google", section, destination: "app", intent: "signup", ...(configuredPageVersion ? { pageVersion: configuredPageVersion } : {}) });
    finishSignIn(user, "google_sso");
  }, [campaign, engage, finishSignIn, section, configuredPageVersion]);

  const resendCode = useCallback(async () => {
    if (isSubmitting || resendCooldown > 0) return;
    const requestId = ++requestIdRef.current;
    setIsSubmitting(true);
    setError("");
    analyticsService.trackCTAClick({ campaign: `${campaign}_resend_code`, label: "Send it again", section, destination: "otp", intent: "signup", ...(configuredPageVersion ? { pageVersion: configuredPageVersion } : {}) });
    const result = await sendOtp(email, "signup");
    if (requestId !== requestIdRef.current) return;
    if (result.success) startCooldown();
    else setError(result.message || "We could not resend the code. Try again.");
    setIsSubmitting(false);
  }, [campaign, email, isSubmitting, resendCooldown, section, sendOtp, startCooldown, configuredPageVersion]);

  const editEmail = useCallback(() => {
    if (isSubmitting) return;
    requestIdRef.current += 1;
    analyticsService.trackCTAClick({ campaign: `${campaign}_edit_email`, label: "Edit it", section, destination: "gate", intent: "signup", ...(configuredPageVersion ? { pageVersion: configuredPageVersion } : {}) });
    setIsSubmitting(false);
    setStep("email");
    setOtpRaw("");
    setError("");
  }, [campaign, isSubmitting, section, configuredPageVersion]);

  const setOtp = useCallback((value: string) => setOtpRaw(value.replace(/\D/g, "").slice(0, 6)), []);
  // Trimmed and lowercased on input: the backend rejects whitespace around the address.
  const setEmail = useCallback((value: string) => {
    engage();
    const normalized = value.trim().toLowerCase();
    setEmailRaw(normalized);
    if (GATE_EMAIL_PATTERN.test(normalized)) clear("email");
  }, [engage, clear]);

  return {
    step, email, nudge, otp, error, isSubmitting, resendCooldown,
    engage, setEmail, setOtp, submitEmail, submitCode,
    completeGoogleSignIn, resendCode, editEmail,
  };
}
